import React, { useState } from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { useTranslation } from 'react-i18next';
import {
  PAYMENT_HUB_RENEWAL_TYPE,
  SUBSCRIPTION_PURCHASE_STATUS,
} from '@src/constants/subscription';
import { ORDER_STATUS } from '@src/constants/package';
import { getPackageName } from '@src/services/package';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import apis from '@src/apis';
import { StyledAutoRenewPackageInfo } from './index.style';
import RenewalPlan from './RenewalPlan';
import NextTimePayment from './NextTimePayment';
import PaymentMethod from './PaymentMethod';

const AutoRenewPackageInfo = ({
  packageExpiryDate,
  subscriptionPurchase,
  packageInfo,
  paymentMethodType,
  orderInfo,
  isInAppPurchase,
}) => {
  const { latestOrder } = useSelector((state) => state.user);
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [showPackageDetail, setShowPackageDetail] = useState(false);
  const [showPopupCancelAutoRenew, setShowPopupCancelAutoRenew] =
    useState(false);

  const { status } = subscriptionPurchase;
  const isAutoRenewalPackage =
    orderInfo?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;

  const shouldShowCancelAutoRenewText =
    status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;

  const checkPendingOrder = (latestOrderData) =>
    latestOrderData && latestOrderData?.status === ORDER_STATUS.PENDING;

  const fetchSubscriptionPurchase = async () => {
    const data = await apis.subscription.getSubscriptionInfo();
    if (data?.status === 1) {
      dispatch(actions.user.updateSubscriptionPurchase(data?.result || {}));
    }
  };

  const handleClickButtonAutoRenew = async () => {
    if (shouldShowCancelAutoRenewText) {
      setShowPopupCancelAutoRenew(true);
      return;
    }

    setLoading(true);
    const response = await apis.subscription.registerAutoRenew({
      paymentMethodId: orderInfo?.paymentMethod?.id,
    });

    const { status: responseStatus, result } = response || {};
    if (responseStatus === 1) {
      const { paymentInfo } = result || {};
      const paymentLink = paymentInfo?.link;
      window.location.assign(paymentLink);
      setLoading(false);
      return;
    }

    setLoading(false);
    dispatch(
      actions.noti.push({
        severity: 'error',
        message: 'registerAutoRenewFail',
      }),
    );
  };

  const handleCancelAutoRenew = async () => {
    setLoading(true);
    const res = await apis.subscription.cancelSubscription();
    if (res?.status !== 1) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'cancelSubscriptionFail',
        }),
      );
    } else {
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'cancelSubscriptionSuccess',
        }),
      );

      fetchSubscriptionPurchase();
    }

    setLoading(false);
    setShowPopupCancelAutoRenew(false);
  };

  const renderButtonAutoRenewContent = () => {
    if (loading) return <CircularProgress size={24} color="info" />;
    if (shouldShowCancelAutoRenewText) return t('cancelAutoRenew');
    return t('enableAutoRenew');
  };

  const renderActionComponents = () => (
    <Box className="cancel-auto-renew-buttons-container">
      <Button
        className="agree"
        onClick={handleCancelAutoRenew}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} color="info" /> : t('agree')}
      </Button>
      <Button
        className="disagree"
        onClick={() => setShowPopupCancelAutoRenew(false)}
        disabled={loading}
      >
        {t('notAgree')}
      </Button>
    </Box>
  );

  return isAutoRenewalPackage ? (
    <>
      <StyledAutoRenewPackageInfo>
        <Box className="auto-renew-title">
          <Typography className="title">{t('autoRenewal')} </Typography>
          <Box
            className="auto-renew-status-on"
            onClick={() => setShowPackageDetail(!showPackageDetail)}
          >
            <Typography className="auto-renew-status">
              {status === SUBSCRIPTION_PURCHASE_STATUS.CANCELED
                ? t('off')
                : t('on')}
            </Typography>
            {showPackageDetail ? (
              <ExpandLess className="expand-icon" />
            ) : (
              <ExpandMore className="expand-icon" />
            )}
          </Box>
        </Box>
        {showPackageDetail && (
          <Box className="auto-renew-content">
            <RenewalPlan
              packageName={getPackageName(
                packageInfo?.name,
                packageInfo?.code,
                language,
                t,
                true,
              )}
            />
            <NextTimePayment packageExpiryDate={packageExpiryDate} />
            <PaymentMethod paymentMethodType={paymentMethodType} />
            {!isInAppPurchase && (
              <Button
                className="button-subscription"
                onClick={handleClickButtonAutoRenew}
                disabled={loading || checkPendingOrder(latestOrder)}
                data-id="change-auto-renewal-status"
                data-current-package-code={packageInfo?.code}
              >
                {renderButtonAutoRenewContent()}
              </Button>
            )}
          </Box>
        )}
      </StyledAutoRenewPackageInfo>
      {showPopupCancelAutoRenew && (
        <NotificationDialogV2
          showIcon={false}
          title={t('confirmCancelAutoRenewTitle')}
          description={t('confirmCancelAutoRenewDescription')}
          open={showPopupCancelAutoRenew}
          onClose={() => setShowPopupCancelAutoRenew(false)}
          actionComponent={renderActionComponents()}
        />
      )}
    </>
  ) : null;
};

export default AutoRenewPackageInfo;
